/**
 * Welcome Page — personalized module portal for non-admin users
 */
import { useAuth } from '@/hooks/useAuth';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { roleLabel } from '@/utils/roleLabel.util';
import {
    Assessment,
    Build,
    Chat,
    Contacts,
    GridView,
    InsertDriveFile,
    Language,
    Lightbulb,
    Note,
    People,
    PersonAdd,
    PlayArrow,
    QuestionAnswer,
    Rocket,
    SentimentSatisfied,
    Settings,
    SmartToy,
    WhatsApp,
    TextFields,
    TrendingUp,
} from '@mui/icons-material';
import { Box, Card, CardContent, Chip, Container, Grid, Typography, alpha } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const moduleMetaMap: Record<
  string,
  { title: string; icon: React.ReactNode; path: string; parent?: string; color: string }
> = {
  // Core — every tenant user has it (see getAccessibleModules), so it leads the portal.
  dashboard: { title: 'Dashboard', icon: <GridView />, path: '/dashboard', color: '#4f46e5' },
  playground: { title: 'Playground', icon: <PlayArrow />, path: '/playground', color: '#8b5cf6' },
  'activity.chatLogs': {
    title: 'Chat Logs',
    icon: <Chat />,
    path: '/activity/chat-logs',
    color: '#3b82f6',
    parent: 'Activity',
  },
  'activity.leads': {
    title: 'Leads',
    icon: <People />,
    path: '/activity/leads',
    color: '#06b6d4',
    parent: 'Activity',
  },
  'analytics.chats': {
    title: 'Chats Analytics',
    icon: <Assessment />,
    path: '/analytics/chats',
    color: '#4f46e5',
    parent: 'Analytics',
  },
  'analytics.topics': {
    title: 'Topics Analytics',
    icon: <TrendingUp />,
    path: '/analytics/topics',
    color: '#6366f1',
    parent: 'Analytics',
  },
  'analytics.sentiment': {
    title: 'Sentiment Analysis',
    icon: <SentimentSatisfied />,
    path: '/analytics/sentiment',
    color: '#ec4899',
    parent: 'Analytics',
  },
  'sources.files': {
    title: 'File Sources',
    icon: <InsertDriveFile />,
    path: '/sources/files',
    color: '#f59e0b',
    parent: 'Sources',
  },
  'sources.text': {
    title: 'Text Sources',
    icon: <TextFields />,
    path: '/sources/text',
    color: '#10b981',
    parent: 'Sources',
  },
  'sources.website': {
    title: 'Website Sources',
    icon: <Language />,
    path: '/sources/website',
    color: '#14b8a6',
    parent: 'Sources',
  },
  'sources.qa': {
    title: 'Q&A Sources',
    icon: <QuestionAnswer />,
    path: '/sources/qa',
    color: '#f97316',
    parent: 'Sources',
  },
  'sources.notion': {
    title: 'Notion Integration',
    icon: <Note />,
    path: '/sources/notion',
    color: '#64748b',
    parent: 'Sources',
  },
  'sources.suggestions': {
    title: 'Suggestions',
    icon: <Lightbulb />,
    path: '/sources/suggestions',
    color: '#eab308',
    parent: 'Sources',
  },
  actions: { title: 'Actions', icon: <Build />, path: '/actions', color: '#ef4444' },
  agents: { title: 'Agents', icon: <SmartToy />, path: '/agents', color: '#8b5cf6' },
  whatsapp: { title: 'WhatsApp Templates', icon: <WhatsApp />, path: '/whatsapp-templates', color: '#22c55e' },
  contacts: { title: 'Contacts', icon: <Contacts />, path: '/contacts', color: '#06b6d4' },
  deploy: { title: 'Deploy', icon: <Rocket />, path: '/deploy', color: '#f97316' },
  userManagement: {
    title: 'User Management',
    icon: <PersonAdd />,
    path: '/user-management',
    color: '#4f46e5',
  },
  settings: { title: 'Settings', icon: <Settings />, path: '/settings', color: '#64748b' },
};

const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const { getAccessibleModules } = useModuleAccess();

  const pathPrefix = user?.role === 'tenant_owner' || user?.role === 'superadmin' ? '/admin' : '';

  const [accessibleModules, setAccessibleModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'tenant_owner' || user?.role === 'superadmin') {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    setAccessibleModules(getAccessibleModules());
    setLoading(false);
  }, [user, navigate, getAccessibleModules]);

  if (loading) return null;

  const visibleModules = accessibleModules.filter((key) => moduleMetaMap[key]);

  return (
    <Box sx={{ minHeight: '100%', py: 4 }}>
      <Container maxWidth="lg">
        {/* Hero */}
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.03em',
              mb: 1.5,
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Welcome back, {user?.firstName}!
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 500, mx: 'auto', mb: 2.5 }}
          >
            Your personalized portal for{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {tenant?.name || 'your organization'}
            </Box>
          </Typography>
          <Chip
            icon={<Settings sx={{ fontSize: '0.875rem !important' }} />}
            label={user?.role === 'tenant_user' ? 'Member' : roleLabel(user?.role)}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 600, textTransform: 'capitalize' }}
          />
        </Box>

        {/* Module cards */}
        <Box
          sx={{
            p: 4,
            borderRadius: 4,
            bgcolor: 'background.paper',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
              Your Modules
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Click any card to navigate to that section.
            </Typography>
          </Box>

          {visibleModules.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  '& svg': { fontSize: 28, color: '#94a3b8' },
                }}
              >
                <GridView />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
                No modules assigned
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Contact your administrator to request module access.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {visibleModules.map((key) => {
                const meta = moduleMetaMap[key]!;
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Card
                      role="button"
                      tabIndex={0}
                      aria-label={`Navigate to ${meta.title}`}
                      onClick={() => navigate(`${pathPrefix}${meta.path}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') navigate(`${pathPrefix}${meta.path}`);
                      }}
                      sx={{
                        cursor: 'pointer',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s cubic-bezier(0.22,1,0.36,1)',
                        '&:hover': {
                          borderColor: alpha(meta.color, 0.4),
                          boxShadow: `0 8px 24px ${alpha(meta.color, 0.15)}`,
                          transform: 'translateY(-3px)',
                          '& .icon-box': { bgcolor: meta.color, color: '#fff' },
                        },
                        '&:focus-visible': { outline: '2px solid #4f46e5', outlineOffset: 2 },
                      }}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          p: '18px !important',
                        }}
                      >
                        <Box
                          className="icon-box"
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            flexShrink: 0,
                            bgcolor: alpha(meta.color, 0.1),
                            color: meta.color,
                            display: 'flex',
                            transition: 'all 0.2s',
                            '& svg': { fontSize: '1.25rem' },
                          }}
                        >
                          {meta.icon}
                        </Box>
                        <Box>
                          {meta.parent && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                fontSize: '0.625rem',
                                display: 'block',
                              }}
                            >
                              {meta.parent}
                            </Typography>
                          )}
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            sx={{ mt: meta.parent ? 0.25 : 0, lineHeight: 1.35 }}
                          >
                            {meta.title}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Welcome;
